"use client";

import * as Sentry from "@sentry/nextjs";
import { useEffect } from "react";
import { Button } from "@/components/ui/button";
import { AlertTriangle } from "lucide-react";

/**
 * Error boundary for the dashboard route.
 * Provides a retry mechanism for transient dashboard load failures.
 */
export default function DashboardError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    Sentry.captureException(error, {
      tags: { route: "dashboard" },
    });
  }, [error]);

  return (
    <div className="flex items-center justify-center min-h-[50vh] p-8">
      <div className="max-w-md text-center space-y-6">
        <AlertTriangle className="mx-auto h-12 w-12 text-destructive" />
        <div className="space-y-2">
          <h2 className="text-2xl font-bold tracking-tight">
            Dashboard unavailable
          </h2>
          <p className="text-muted-foreground">
            We couldn&#39;t load your dashboard. This might be a temporary
            issue.
          </p>
          {error.digest && (
            <p className="text-xs text-muted-foreground font-mono">
              Error ID: {error.digest}
            </p>
          )}
        </div>
        <Button onClick={() => reset()} variant="default">
          Retry
        </Button>
      </div>
    </div>
  );
}